import { AutoComplete, Button, Form, Input, Modal, Select } from "antd";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { createRate, updateRate } from "../../api/rate";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { useEffect, useState } from "react";
import { useForm } from "antd/es/form/Form";
import UploadImages from "../products/upload";
import { getShippingAddress } from "../../api/ship";

const AddRate = ({
  open = false,
  setOpen,
  data,
  type = "giftcard",
  allRates = [],
}) => {
  const queryClient = useQueryClient();
  const [form] = useForm();
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState("");
  const [options, setOptions] = useState([]);
  const [rateType, setRateType] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);

  const { data: addresses } = useQuery("shipping-address", getShippingAddress);

  const mutation = useMutation(data ? updateRate : createRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(type);
      showSuccessToast(data ? "Changes updated" : "Data added successfully");
      setOpen(false);
      form.resetFields();
    },
    onError: (e) => {
      showErrorToast(e.message);
    },
  });

  const onFinish = (values) => {
    if (type !== "shipping" && !values.min && !values.max) {
      showErrorToast("Range not entered");
      return;
    }
    mutation.mutate({
      ...(data && { id: data._id }),
      ...values,
      category,
      ...(values?.products && { products: values?.products?.split(",") }),
      type,
      ...(type !== "shipping" && {
        range: { min: Number(values.min), max: Number(values.max) },
      }),
      ...(images.length && { image: images[0]?.originFileObj }),
    });
  };

  const handleSearch = (value) => {
    if (!value) {
      setOptions([]);
      return;
    }

    const filteredName = allRates.filter((item) =>
      item._id.toLowerCase().includes(value.toLowerCase())
    );
    const recomposed = filteredName.map((item) => ({ value: item._id }));
    setOptions(recomposed);
  };

  const handleCountrySearch = (value) => {
    if (!value) {
      setCountryOptions([]);
      return;
    }

    const recomposeCountries = [
      ...new Set(
        allRates.flatMap((item) => item.record.map((rec) => rec.country))
      ),
    ];
    const filteredName = recomposeCountries.filter((item) =>
      item.toLowerCase().includes(value.toLowerCase())
    );
    const recomposed = filteredName.map((item) => ({ value: item }));
    setCountryOptions(recomposed);
  };

  useEffect(() => {
    if (data) {
      form.setFields(
        Object.keys(data).map((key) => ({ name: key, value: data[key] }))
      );
      if (data?.range?.min && data?.range?.max) {
        form.setFieldValue("min", data.range.min);
        form.setFieldValue("max", data.range.max);
      }
    }
  }, [data]);

  return (
    <Modal
      title={
        type === "giftcard"
          ? "Add Gift Card"
          : type === "shipping"
          ? "Add Shipping Rate"
          : type === "sell-giftcard"
          ? "Sell Gift Card"
          : "Add Crypto rate"
      }
      open={open}
      footer={null}
      onCancel={() => {
        form.resetFields();
        setOpen(false);
      }}
    >
      {type === "sell-giftcard" && (
        <UploadImages setImages={setImages} maxLength={1} />
      )}
      <Form layout="vertical" onFinish={onFinish} form={form}>
        <Form.Item name="name" label="Name">
          {type === "shipping" ? (
            <Input placeholder="Enter Identifier" />
          ) : (
            <AutoComplete
              placeholder={
                type === "crypto" || type === "sell-crypto"
                  ? "Bitcoin"
                  : "Amazon"
              }
              onSearch={handleSearch}
              onSelect={(value) =>
                setCategory((prev) =>
                  prev.split("/").length
                    ? `${prev.split("/")[0]}/${value}`
                    : value
                )
              }
              onChange={(e) =>
                setCategory((prev) =>
                  prev.split("/").length ? `${prev.split("/")[0]}/${e}` : e
                )
              }
              options={options}
              allowClear
            />
          )}
        </Form.Item>

        {!["crypto", "sell-crypto", "shipping"].includes(type) && (
          <Form.Item name="country" label="Country">
            <AutoComplete
              placeholder="Select Country"
              onSearch={handleCountrySearch}
              options={countryOptions}
              onSelect={(e) =>
                setCategory((prev) =>
                  prev.split("/").length > 1
                    ? `${e}/${prev.split("/")[1]}`
                    : `${e}/${prev}`
                )
              }
              onChange={(e) =>
                setCategory((prev) =>
                  prev.split("/").length > 1
                    ? `${e}/${prev.split("/")[1]}`
                    : `${e}/${prev}`
                )
              }
              allowClear
            />
          </Form.Item>
        )}

        {type === "shipping" && (
          <Form.Item name="state" label="Address">
            <Select>
              {(addresses || []).map((add, k) => {
                return (
                  <Select.Option
                    value={JSON.stringify({
                      _id: add._id,
                      line1: add.line1,
                      city: add.city,
                      state: add.state,
                      country: add.country,
                    })}
                    key={k}
                  >
                    {add.line1} {add.city}, {add.state}, {add.country}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        )}

        {type === "shipping" && (
          <>
            <Form.Item name="category" label="Category">
              <AutoComplete
                placeholder="Enter Category"
                onSearch={handleSearch}
                onSelect={(value) => setCategory(() => value)}
                onChange={(e) =>
                  setCategory(() =>e)
                }
                options={options}
                allowClear
              />
            </Form.Item>

            <Form.Item
              name="products"
              label="Products (For multi products, seperate with comma)"
            >
              <Input placeholder="Laptop, Mobile Phone" />
            </Form.Item>

            <Form.Item name="rateType" label="Rate Type">
              <Select className="" onChange={(e) => setRateType(e)}>
                <Select.Option value="fixed">Fixed</Select.Option>
                <Select.Option value="dynamic">Dynamic</Select.Option>
              </Select>
            </Form.Item>
          </>
        )}

        {type === "giftcard" && (
          <>
            <Form.Item name="category" label="Category">
              <Input placeholder="Enter category" />
            </Form.Item>
          </>
        )}

        {type === "sell-giftcard" && (
          <>
            <label className="mb-5">Category</label>
            <Input
              placeholder="Enter category"
              value={category}
              onChange={(e) => setCategory(() => e.target.value)}
            />
          </>
        )}

        {type !== "shipping" && (
          <>
            <p className="mt-3">Range</p>
            <div className="flex gap-5 mt-2 w-full">
              <Form.Item
                name="min"
                label=""
                className="w-[50%]"
                rules={[{ required: true, message: "Enter rate min" }]}
              >
                <Input placeholder="1" type="number" />
              </Form.Item>

              <Form.Item
                name="max"
                label=""
                className="w-[50%]"
                rules={[{ required: true, message: "Enter rate min" }]}
              >
                <Input placeholder="100" type="number" />
              </Form.Item>
            </div>
          </>
        )}

        {type === "shipping" && rateType === "dynamic" && (
          <Form.Item name="weight" label="Weight (in lbs)">
            <Input placeholder="100" type="number" />
          </Form.Item>
        )}

        <Form.Item
          name="rate"
          label="Rate"
          rules={[{ required: true, message: "Enter rate" }]}
        >
          <Input placeholder="100" type="number" />
        </Form.Item>

        <Button
          className="bg-dark text-white rounded-[5px] font-bold text-[15px]"
          htmlType="submit"
          loading={mutation.isLoading}
        >
          Save Changes
        </Button>
      </Form>
    </Modal>
  );
};

export default AddRate;
